import React, { useEffect } from 'react';
import { useQuizWebSocket } from '../../hooks/useQuizWebSocket';
import { resumeAudioContext } from '../../utils/soundEffects';
import QuizLobby from './QuizLobby';
import QuizGame from './QuizGame';
import QuizResults from './QuizResults';
import './quiz-battle.css';

/**
 * QuizBattleMode — Orchestrator
 *
 * Self-paced race: all questions delivered upfront,
 * each player progresses independently.
 */
function QuizBattleMode({ onBack }) {
  const ws = useQuizWebSocket();

  useEffect(() => {
    const resume = () => {
      resumeAudioContext();
      window.removeEventListener('click', resume);
    };
    window.addEventListener('click', resume);
    return () => window.removeEventListener('click', resume);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && ws.phase === 'idle') {
        onBack();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [ws.phase, onBack]);

  const handleBackToMenu = () => {
    ws.disconnect();
    onBack();
  };

  // The lobby / game / results components each render their own full-screen
  // Inkwell layout (paper background, ink-header), so we render them directly
  // without a wrapper. Earlier versions wrapped these in a flex-centred dark
  // `.quiz-battle` stage, which made the content render as a narrow column on
  // desktop ("mobile-on-desktop" effect).

  // From the lobby / idle, leaving = exit Battle entirely (disconnect + onBack).
  // QuizLobby's "← Modes" button calls onLeave; we want it wired to a clean exit.
  if (ws.phase === 'idle' || ws.phase === 'lobby') {
    return (
      <QuizLobby
        phase={ws.phase}
        roomCode={ws.roomCode}
        players={ws.players}
        isHost={ws.isHost}
        error={ws.error}
        onCreateRoom={ws.createRoom}
        onJoinRoom={ws.joinRoom}
        onStartGame={ws.startGame}
        onLeave={handleBackToMenu}
      />
    );
  }

  if (ws.phase === 'finished') {
    return (
      <QuizResults
        finalScores={ws.finalScores}
        playerId={ws.playerId}
        isHost={ws.isHost}
        questions={ws.questions}
        hostKeys={ws.hostKeys}
        hostAnswers={ws.hostAnswers}
        gameType={ws.gameType}
        onPlayAgain={ws.rematch}
        onBackToMenu={handleBackToMenu}
      />
    );
  }

  // countdown / playing / waiting-for-others
  return (
    <QuizGame
      phase={ws.phase}
      gameType={ws.gameType}
      questions={ws.questions}
      currentIndex={ws.currentIndex}
      timeLimit={ws.timeLimit}
      countdown={ws.countdown}
      lastResult={ws.lastResult}
      totalCorrect={ws.totalCorrect}
      penaltyMs={ws.penaltyMs}
      playerProgress={ws.playerProgress}
      playerId={ws.playerId}
      isHost={ws.isHost}
      hostKeys={ws.hostKeys}
      hostAnswers={ws.hostAnswers}
      onSubmitAnswer={ws.submitAnswer}
      onAdvance={ws.advanceQuestion}
    />
  );
}

export default QuizBattleMode;
